import {Component, Input, OnInit} from '@angular/core';
import AudioTrackModel from '../../../../models/audio-track.model';
import {Store} from '@ngrx/store';
import {
    selectActualVideoState,
    selectCurrentTimeFromState,
    selectNavigateToTimeFromState,
    selectUserVolumeFromState
} from '../../../shared/stores/root-store/selectors/video.selector';
import {filter, flatMap, map} from 'rxjs/operators';
import VideoState from '../../../shared/stores/root-store/video.state';
import {VideoStateEnum} from '../../../../enums/video-state.enum';
import {SetAudioTrackIsLoaded, SetAudioTrackIsPlaying} from '../../../shared/stores/root-store/actions/audio.action';
import {AudioState} from '../../../../enums/audio-state.enum';
import {throwError} from 'rxjs';
import {SetIsCurrentSceneLoaded} from '../../../shared/stores/root-store/actions/video.action';

@Component({
    selector: 'eocm-apple-audio-track',
    templateUrl: './apple-audio-track.component.html',
    styleUrls: ['./apple-audio-track.component.scss']
})
export class AppleAudioTrackComponent implements OnInit {
    @Input() audio: AudioTrackModel;
    @Input() audioContext: AudioContext;

    time = 0;
    gainNode = null;
    source = null;
    isInitialized = false;
    state = AudioState.STOPPED;
    userVolume = 100;

    startOffset: number;
    startTime: number;
    currentRate: number;
    prevTime: number;

    syncInterval: ReturnType<typeof setInterval>;
    syncTolerance = 0.1; // s

    constructor(private store: Store<{ video: VideoState }>) {}

    ngOnInit() {
        this.store.select(selectCurrentTimeFromState)
            .pipe(
                flatMap(time => {
                    this.time = time;

                    return this.store.select(selectActualVideoState);
                }),
                map(state => this.playOrPauseAudio(state))
            ).subscribe()
        ;

        this.store
            .select(selectUserVolumeFromState)
            .subscribe(volume => {
                this.userVolume = volume;

                if (this.gainNode) {
                    this.gainNode.gain.value = this.calculateUserVolume();
                }
            })
        ;

        if (this.audio.synchronized) {
            this.store.select(selectNavigateToTimeFromState).pipe(
                filter(navTime => navTime !== 0),
                map(time => {
                    this.time = time;

                    return time;
                }),
                filter(time => time > this.audio.time.start && time < this.audio.time.end),
                map(() => {
                    this.setCurrentTime();
                })
            ).subscribe();
        }

        this.loadAudioBuffer();
    }

    loadAudioBuffer() {
        return this.decodeAudio(this.audio.url).then((buffer) => {

            const source = this.audioContext.createBufferSource();
            const gainNode = this.audioContext.createGain();

            source.buffer = buffer;

            source.connect(gainNode);
            gainNode.connect(this.audioContext.destination);

            if (!this.audio.synchronized) {
                source.loop = true;
            }

            this.source = source;
            this.gainNode = gainNode;

            this.gainNode.gain.value = this.calculateUserVolume();
            this.isInitialized = true;

            this.store.dispatch(SetAudioTrackIsLoaded({
                isLoaded: true, audioTrackId: this.audio.id
            }));
        }, (reason) => {
            return throwError(reason);
        });
    }

    playAudio() {
        if (this.state === AudioState.PLAYING) {
            return;
        }

        if (this.state === AudioState.PAUSED) {
            this.audioContext.resume();
        }

        if (this.state === AudioState.STOPPED) {
            const currentTime = this.time > 0 ? this.time - this.audio.time.start : 0;

            this.source.start(0, parseFloat(currentTime.toFixed(4)));
        }

        if (this.audio.synchronized && !this.syncInterval) {
            this.syncInterval = setInterval(() => this.syncHandler(), 25);
        }

        this.setInitialTimes();

        this.state = AudioState.PLAYING;
        this.store.dispatch(SetAudioTrackIsPlaying({
            isPlaying: true, audioTrackId: this.audio.id
        }));
    }

    pauseAudio() {
        if (this.state === AudioState.PLAYING) {
            this.audioContext.suspend();
            this.state = AudioState.PAUSED;

            this.clearSyncInterval();
        }
    }

    stopAudio() {
        if (!this.isInitialized || this.state === AudioState.STOPPED) {
            return;
        }

        this.source.stop(0);
        this.source.disconnect();
        this.state = AudioState.STOPPED;

        const newSource = this.audioContext.createBufferSource();

        newSource.buffer = this.source.buffer;
        newSource.connect(this.gainNode);

        if (!this.audio.synchronized) {
            newSource.loop = true;
        }

        this.source = newSource;
        this.gainNode.gain.value = this.calculateUserVolume();

        this.clearSyncInterval();
        this.store.dispatch(SetAudioTrackIsPlaying({
            isPlaying: false, audioTrackId: this.audio.id
        }));
    }

    setCurrentTime() {
        this.store.dispatch(SetAudioTrackIsLoaded({
            isLoaded: false, audioTrackId: this.audio.id
        }));
        this.stopAudio();
        this.playAudio();
        this.store.dispatch(SetAudioTrackIsLoaded({
            isLoaded: true, audioTrackId: this.audio.id
        }));
    }

    calculateUserVolume() {
        return this.audio.volume * this.userVolume / 100;
    }

    shouldPlayAudio() {
        return this.time > this.audio.time.start && this.time < this.audio.time.end;
    }

    playOrPauseAudio(videoState: VideoStateEnum) {
        if (videoState === VideoStateEnum.Interaction && this.shouldPlayAudio()) {
            if (!this.audio.synchronized) {
                this.playAudio();
            } else {
                this.pauseAudio();
            }

            return;
        }

        if (this.shouldPlayAudio()) {
            switch (videoState) {
                case VideoStateEnum.Playing:
                    this.playAudio();
                    break;
                case VideoStateEnum.Ended:
                    this.stopAudio();
                    break;
                case VideoStateEnum.Pause:
                case VideoStateEnum.Buffering:
                case VideoStateEnum.UserPause:
                    this.pauseAudio();
                    break;
            }
        } else {
            this.stopAudio();
        }
    }

    private decodeAudio(url: string): Promise<AudioBuffer> {
        return new Promise((resolve, reject) => {
            const request = new XMLHttpRequest();

            request.open('GET', url, true);
            request.responseType = 'arraybuffer';

            request.addEventListener('load', () => {
                this.decodeAudioBuffer({
                    buffer: request.response,
                    sync: 0,
                    tried: 0
                } , resolve, reject);
            });

            request.addEventListener('error', reject, false);
            request.send();
        });
    }

    private synchronizeStream(audioBuffer: {buffer: ArrayBuffer, sync: number, tried: number}) {
        let sync = audioBuffer.sync;
        const buffer8 = new Uint8Array(audioBuffer.buffer);
        const syncAttempt = 1;

        while (syncAttempt) {
            audioBuffer.tried++;

            sync = buffer8.indexOf(0xFF, sync);

            // @ts-ignore
            // eslint-disable-next-line no-bitwise
            if (sync === -1 || (buffer8[sync + 1] & 0xE0 === 0xE0)) {
                break;
            }

            sync++;
        }

        if (sync !== -1) {
            const tempBuffer = audioBuffer.buffer.slice(sync);

            delete (audioBuffer.buffer); audioBuffer.buffer = null;
            audioBuffer.buffer = tempBuffer;
            audioBuffer.sync = sync;

            return true;
        }

        return false;
    }

    private decodeAudioBuffer(
        audioBuffer: {buffer: ArrayBuffer, sync: number, tried: number},
        resolve: (buffer: AudioBuffer) => any,
        reject: (error: Error) => any
    ) {
        const successCallback = (buffer) => {
            resolve(buffer);
        };
        const errorCallback = (err) => {
            if (err === null && this.synchronizeStream(audioBuffer)) {
                // todo - Add event which will store each bitwise operation on audio file by using audioBuffer.tried
                this.decodeAudioBuffer(audioBuffer, resolve, reject);
            } else {
                reject(new Error('Cant decode Audio. File can be corrupted'));
            }
        };

        this.audioContext.decodeAudioData(audioBuffer.buffer, successCallback, errorCallback);
    }

    private clearSyncInterval() {
        if (this.syncInterval) {
            clearInterval(this.syncInterval);
            this.syncInterval = null;
        }
    }

    private setInitialTimes() {
        this.startOffset = this.audioContext.currentTime;
        this.currentRate = this.source.playbackRate.value;
        this.startTime = this.time;
    }

    private syncHandler() {
        if (this.synchronizePlayback()) {
            this.syncTime();
        }

        this.prevTime = this.time;
    }

    private synchronizePlayback() {
        if (this.prevTime === this.time) {
            this.audioContext.suspend();
            this.store.dispatch(SetIsCurrentSceneLoaded({
                isCurrentSceneLoaded: false
            }));

            return false;
        } else if(this.prevTime !== this.time && this.audioContext.state === 'suspended') {
            this.audioContext.resume();
            this.store.dispatch(SetIsCurrentSceneLoaded({
                isCurrentSceneLoaded: true
            }));
        }

        return true;
    }

    private syncTime() {
        const videoTime = this.time;
        const audioTime = this.startTime + (this.audioContext.currentTime - this.startOffset) * this.currentRate;
        const diff = audioTime - videoTime;

        if (diff > this.syncTolerance) {
            this.source.playbackRate.value = 0.9;
        } else if (diff < -this.syncTolerance) {
            this.source.playbackRate.value = 1.1;
        } else if (!isNaN(diff)) {
            this.source.playbackRate.value = 1.0;
        }

        if (this.source.playbackRate.value !== this.currentRate) {
            this.setInitialTimes();
        }
    }
}
